import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { PaymentInterval } from "./types";

interface BillingToggleProps {
	availableIntervals: PaymentInterval[];
	activeInterval: PaymentInterval;
	onChange: (interval: PaymentInterval) => void;
}

/** Annual payment-frequency chips, in display order. */
const ANNUAL_FREQUENCIES: { interval: PaymentInterval; labelKey: string }[] = [
	{ interval: "year", labelKey: "usage.subscription.checkout.payFrequency.yearly" },
	{ interval: "semi-annual", labelKey: "usage.subscription.checkout.payFrequency.sixMonths" },
	{ interval: "quarter", labelKey: "usage.subscription.checkout.payFrequency.threeMonths" },
];

/**
 * Monthly ⇄ Annually switch with a "Save 10%" pill. When the user may pay in
 * installments, the annual contract exposes Yearly / Every 6 months / Every 3
 * months chips beneath the switch.
 */
export const BillingToggle: FunctionComponent<BillingToggleProps> = ({
	availableIntervals,
	activeInterval,
	onChange,
}) => {
	const { t } = useTranslation();

	const isAnnual = activeInterval !== "month";
	const showSavePill = availableIntervals.includes("year");
	const annualFrequencies = ANNUAL_FREQUENCIES.filter((f) =>
		availableIntervals.includes(f.interval)
	);
	const showFrequencyChips = isAnnual && annualFrequencies.length > 1;

	const noteKey =
		activeInterval === "month"
			? "usage.subscription.checkout.billingNote.monthly"
			: activeInterval === "year"
				? "usage.subscription.checkout.billingNote.annual"
				: activeInterval === "quarter"
					? "usage.subscription.intervalSubcopy.quarter"
					: "usage.subscription.intervalSubcopy.semiAnnual";

	const handleSwitch = () => onChange(isAnnual ? "month" : "year");

	return (
		<div className="flex flex-col items-center gap-2.5">
			<div className="flex items-center gap-3.5">
				<span
					className={classNames(
						"text-sm font-semibold cursor-pointer select-none transition-colors",
						isAnnual ? "text-text-meta" : "text-text"
					)}
					onClick={() => onChange("month")}
				>
					{t("usage.subscription.intervals.monthly")}
				</span>

				<button
					aria-label={t("usage.subscription.intervals.annually")}
					className={classNames(
						"relative w-[54px] h-[30px] rounded-full transition-colors flex-shrink-0",
						isAnnual ? "bg-blue-action" : "bg-border-light"
					)}
					type="button"
					onClick={handleSwitch}
				>
					<span
						className={classNames(
							"absolute top-[3px] left-[3px] w-6 h-6 rounded-full bg-white shadow-mb-card transition-transform",
							isAnnual ? "translate-x-6" : "translate-x-0"
						)}
					/>
				</button>

				<span
					className={classNames(
						"text-sm font-semibold cursor-pointer select-none transition-colors",
						isAnnual ? "text-text" : "text-text-meta"
					)}
					onClick={() => onChange("year")}
				>
					{t("usage.subscription.intervals.annually")}
				</span>

				{showSavePill && (
					<span className="text-xs font-semibold text-blue-action bg-subscription-save-pill px-2.5 py-1 rounded-full">
						{t("usage.subscription.checkout.savePill")}
					</span>
				)}
			</div>

			{showFrequencyChips && (
				<div className="flex items-center gap-2">
					<span className="text-xs font-medium text-text-meta">
						{t("usage.subscription.checkout.payFrequency.label")}
					</span>
					{annualFrequencies.map(({ interval, labelKey }) => (
						<button
							key={interval}
							className={classNames(
								"px-3 py-1.5 rounded-lg text-sm transition-colors",
								activeInterval === interval
									? "bg-blue-action text-white font-bold"
									: "text-text-meta font-normal hover:bg-background-light"
							)}
							type="button"
							onClick={() => onChange(interval)}
						>
							{t(labelKey)}
						</button>
					))}
				</div>
			)}

			<p className="text-center text-sm text-text-meta leading-relaxed max-w-[520px]">
				{t(noteKey)}
			</p>
		</div>
	);
};
